import { useCallback, useContext, useLayoutEffect, useRef, useState } from "react";
import { SurfaceVisibility } from "../panel/SurfaceHost";
import { SurfaceConversationFocus } from "../panel/SurfaceIntent";
import type { PanelEntry } from "../lib/api";
import { ChatPage } from "../pages/ChatPage";
import { SurfaceWorkspace } from "../panel/SurfaceWorkspace";
import type { SurfaceIntentDraft } from "../panel/SurfaceIntent";

/** Each embedded Agent keeps its own API, conversation and registered pages. */
export function AgentWorkspace({ panels, version, chatVersion = version, registryVersion = 0, connected, queueAvailable = false, onToast,
  sessionId = "s-owner", panelKey, context, createRequest = false, navigationVisit, onContextChange, incomingSurfaceIntent, onSurfaceIntentPlaced }: {
  panels: PanelEntry[];
  version: number;
  /** What re-reads the conversation beside the work surface; `version` when not given. */
  chatVersion?: number;
  registryVersion?: number;
  connected: boolean;
  queueAvailable?: boolean;
  onToast: (text: string, tone: "good" | "critical") => void;
  sessionId?: string;
  panelKey?: string;
  context?: Record<string, string>;
  createRequest?: boolean;
  navigationVisit?: number;
  onContextChange?: (panelKey: string, context: Record<string, string>) => void;
  incomingSurfaceIntent?: SurfaceIntentDraft | null;
  onSurfaceIntentPlaced?: (commandId: string) => void;
}) {
  const chat = useRef<HTMLElement>(null);
  const surface = useRef<HTMLDivElement>(null);
  const [part, setPart] = useState<"chat" | "work">("chat");
  const focusAfterSwitch = useRef(false);
  const openChat = useCallback(() => {
    if (part === "chat") chat.current?.querySelector("textarea")?.focus();
    else { focusAfterSwitch.current = true; setPart("chat"); }
  }, [part]);
  useLayoutEffect(() => {
    if (part === "chat" && focusAfterSwitch.current) {
      focusAfterSwitch.current = false;
      chat.current?.querySelector("textarea")?.focus();
    }
  }, [part]);
  const visible = useContext(SurfaceVisibility);
  useLayoutEffect(() => { surface.current?.toggleAttribute("inert", !connected); }, [connected]);
  return <SurfaceConversationFocus.Provider value={openChat}><div className="agent-workspace">
    <nav className="parallel-part-tabs" aria-label="查看这个 IP"><button type="button" className="btn" aria-pressed={part === "chat"} onClick={() => setPart("chat")}>对话</button><button type="button" className="btn" aria-pressed={part === "work"} onClick={() => setPart("work")}>作品与工作</button></nav>
    <SurfaceVisibility.Provider value={visible && part === "work"}>
    <div className="embedded-work-surface" ref={surface} style={{ display: part === "work" ? undefined : "none" }}>
      <SurfaceWorkspace sessionId={sessionId} panelKey={panelKey} context={context} createRequest={createRequest} navigationVisit={navigationVisit} onContextChange={onContextChange}
        active={connected} version={version} onToast={onToast}
        onOpenChat={openChat} />
    </div></SurfaceVisibility.Provider>
    <aside className="embedded-work-conversation" ref={chat} style={{ display: part === "chat" ? undefined : "none" }} aria-label="这个 IP 的对话">
      <ChatPage sessionId={sessionId} onPickSession={() => {}} chatVersion={chatVersion} registryVersion={registryVersion}
        connected={connected} queueAvailable={queueAvailable} ownerMode dock onToast={onToast} panels={panels}
        incomingSurfaceIntent={incomingSurfaceIntent} onSurfaceIntentPlaced={onSurfaceIntentPlaced} />
    </aside>
  </div></SurfaceConversationFocus.Provider>;
}
